import { FC, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { modifierKey } from '@app/utilities/functions';
import styles from './KeyboardShortcutsPopover.module.scss';

type Shortcut = {
  keys: string[];
  label: string;
};

let cachedReadOnlyShortcuts: Shortcut[] | null = null;
let cachedShortcuts: Shortcut[] | null = null;

const getReadOnlyShortcuts = (): Shortcut[] => {
  if (!cachedReadOnlyShortcuts) {
    cachedReadOnlyShortcuts = [
      { keys: [modifierKey, 'E'], label: 'Open script editor' },
      { keys: [modifierKey, 'G'], label: 'Toggle full screen' },
      { keys: [modifierKey, 'I'], label: 'Toggle inputs view' },
      { keys: [modifierKey, 'J'], label: 'Explain code' },
      { keys: [modifierKey, 'R'], label: 'Run script' }
    ];
  }
  return cachedReadOnlyShortcuts;
};

const getShortcuts = (): Shortcut[] => {
  if (!cachedShortcuts) {
    cachedShortcuts = [
      ...getReadOnlyShortcuts(),
      { keys: [modifierKey, 'B'], label: 'Beautify code' },
      { keys: [modifierKey, 'H'], label: 'AI Assist' },
      { keys: [modifierKey, 'S'], label: 'Save' }
    ].sort((a, b) => a.label.localeCompare(b.label));
  }
  return cachedShortcuts;
};

export type KeyboardShortcutsPopoverProps = {
  /** Whether to include the edit-only shortcuts (Beautify, AI Assist). */
  readOnly?: boolean;
};

/**
 * A small "?" button that toggles a popover listing all keyboard
 * shortcuts available in the script editor. Opens on hover and
 * can be pinned open by clicking.
 */
export const KeyboardShortcutsPopover: FC<KeyboardShortcutsPopoverProps> = ({
  readOnly = false
}) => {
  const [isHovered, setIsHovered] = useState(false);
  const [isPinned, setIsPinned] = useState(false);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const isOpen = isHovered || isPinned;
  const visibleShortcuts = (readOnly) ? getReadOnlyShortcuts() : getShortcuts();

  // Calculate fixed position from the trigger button
  const getPopoverStyle = (): React.CSSProperties => {
    if (!triggerRef.current) {
      return {};
    }
    const rect = triggerRef.current.getBoundingClientRect();
    return {
      left: rect.left,
      top: rect.top - 8
    };
  };

  return (
    <div className={styles.wrapper}>
      <button
        className={styles.trigger}
        onClick={() => setIsPinned(!isPinned)}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
        ref={triggerRef}
        type="button"
      >
        ?
      </button>
      {isOpen && createPortal(
        <>
          {isPinned && (
            <div className={styles.overlay} onClick={() => setIsPinned(false)} />
          )}
          <div
            className={styles.popover}
            style={getPopoverStyle()}
          >
            <div className={styles.header}>
              <h3>Keyboard Shortcuts</h3>
            </div>
            <div className={styles.list}>
              {visibleShortcuts.map((shortcut) => (
                <div key={shortcut.label} className={styles.item}>
                  <span className={styles.label}>{shortcut.label}</span>
                  <div className={styles.keys}>
                    {shortcut.keys.map((key) => (
                      <span key={key} className={styles.key}>{key}</span>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </>,
        document.body
      )}
    </div>
  );
};
